import { Injectable } from '@angular/core';
import { filter, map, Observable, of, Subject, switchMap, take, throwError, withLatestFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { environment } from '@environments/environment';
import { ResponseObjectSuccess, ResponseSuccess } from '@interfaces/response-success';
import { AuthenticationState } from '@store/reducers/authentication.reducer';
import { selectAuthenticationIsReseller, selectCredential } from '@store/selectors/authentication.selectors';
import { MenuItem } from 'primeng/api';
import { transformDropdown, transformRoleUpsert } from '@core/helpers';
import { Credential, Rol, RolForm } from '@core/models';
import { selectRoleList } from '@store/selectors';
import { MenuItemExtended } from '@core/interfaces/menu-item-extended';

@Injectable({
  providedIn: 'root',
})
export class RoleService {
  credential$: Observable<Credential | null>;
  roleList$: Observable<Rol[] | null>;

  destroyTab$ = new Subject<void>();

  private creationCompletedSource = new Subject<void>();
  creationCompleted$ = this.creationCompletedSource.asObservable();

  private updateCompletedSource = new Subject<void>();
  updateCompleted$ = this.updateCompletedSource.asObservable();

  private findAllCompletedSource = new Subject<void>();
  findAllCompleted$ = this.findAllCompletedSource.asObservable();

  private changeTabSource = new Subject<MenuItemExtended|null>();
  changeTab$ = this.changeTabSource.asObservable();

  private toggleDialogSource = new Subject<boolean>();
  toggleDialog$ = this.toggleDialogSource.asObservable();

  private closeDialogSource = new Subject<void>();
  closeDialog$ = this.closeDialogSource.asObservable();

  constructor(
    private http: HttpClient,
    private store: Store<AuthenticationState>,
  ) {
    this.credential$ = this.store.select(selectCredential);
    this.roleList$ = this.store.select(selectRoleList);
  }

  onDestroyTab(): void {
    this.destroyTab$.next();
    this.destroyTab$.complete();
    this.destroyTab$ = new Subject<void>();
  }

  creationCompleted(): void {
    this.creationCompletedSource.next();
    this.closeDialog();
  }
  
  updateCompleted(): void {
    this.updateCompletedSource.next();
    this.closeDialog();
  }

  findAllCompleted(): void {
    this.findAllCompletedSource.next();
  }

  changeTab(event: MenuItemExtended): void {
    setTimeout(() => this.changeTabSource.next(event));
  }

  toggleDialog(event: boolean): void {
    this.toggleDialogSource.next(event);
  }

  closeDialog(): void {
    this.closeDialogSource.next();
  }

  find(id?: string): Observable<ResponseSuccess<Rol>> {
    return this.credential$.pipe(
      filter((credential) => !!credential),
      switchMap((credential) => {
        if (!credential?.token && !credential?.granted)
          return throwError(() => new Error('No credential granted'));

        if (!id)
          return of({ success: true, data: [], count: 0 }) as Observable<ResponseSuccess<Rol>>;
        
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.get<ResponseSuccess<Rol>>(
          `${environment.microSeguridades}/api/v1/pos/reseller/rol/${id}`,
          { headers: { Authorization: token } },
        );
      }),
      take(1),
    );
  }

  findAll({ resellerId, search }: { resellerId?: string, search?: string }): Observable<ResponseSuccess<Rol>> {
    return this.credential$.pipe(
      filter((credential) => credential !== null),
      withLatestFrom(
        this.store.select(selectAuthenticationIsReseller)
      ),
      switchMap(([ credential, isReseller ]) => {
        if (!credential?.granted)
          return throwError(() => new Error('No credential granted'));

        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        const params = {
          ...(!!resellerId && { resellerId }),
          ...(!!isReseller && { resellerId: credential.idUsuario }),
          ...(!!search && { nombre: search }),
        };

        return this.http.get<ResponseSuccess<Rol>>(
          `${environment.microSeguridades}/api/v1/pos/reseller/rol`,
          {
            headers: { Authorization: token },
            params: { ...params,  },
          },
        );
      }),
      take(1),
    );
  }

  filter(text: string): Observable<Rol[]> {
    return this.roleList$.pipe(
      filter((roleList): roleList is Rol[] => roleList !== null && roleList.length > 0),
      map((roleList) => roleList.filter((role) => role.nombre.toLowerCase().includes(text.toLowerCase()))),
      take(1),
    );
  }

  create(roleForm: RolForm): Observable<ResponseObjectSuccess<Rol>> {
    return this.credential$.pipe(
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.post<ResponseObjectSuccess<Rol>>(
          `${environment.microSeguridades}/api/v1/pos/reseller/rol`,
          transformRoleUpsert({ ...roleForm }),
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  update(id: string, roleForm: RolForm): Observable<ResponseObjectSuccess<Rol>> {
    return this.credential$.pipe(
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.patch<ResponseObjectSuccess<Rol>>(
          `${environment.microSeguridades}/api/v1/pos/reseller/rol/${id}`,
          transformRoleUpsert({ ...roleForm }),
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  remove(id: string): Observable<Response> {
    return this.credential$.pipe(
      filter((credential) => !!credential?.granted),
      switchMap((credential) => {
        const token =
          credential && credential?.token
            ? credential.token
            : credential?.apiKey
              ? credential?.apiKey
              : '';

        return this.http.delete<Response>(
          `${environment.microSeguridades}/api/v1/pos/reseller/rol/${id}`,
          { headers: { Authorization: token } }
        );
      }),
      take(1),
    );
  }

  getDropdown(): Observable<MenuItem[]> {
    return this.roleList$.pipe(
      filter((roles): roles is Rol[] => !!roles),
      map((roles) => transformDropdown(roles, 'id', 'nombre')),
    );
  }
}
